// Boss Fights (ADR-0025) admin api client.

import { api } from '../../../lib/api';
import type { Boss, BossStatus, BossHpPoolTicket } from './types';

export interface BossListParams {
  team_id?: string;
  status?: BossStatus;
}

export const bossesApi = {
  list(params: BossListParams = {}): Promise<{ bosses: Boss[] }> {
    const q = new URLSearchParams();
    if (params.team_id) q.set('team_id', params.team_id);
    if (params.status) q.set('status', params.status);
    const qs = q.toString();
    return api(`/api/admin/bosses${qs ? `?${qs}` : ''}`);
  },

  get(id: string): Promise<{ boss: Boss }> {
    return api(`/api/admin/bosses/${id}`);
  },

  create(input: {
    team_id: string;
    name: string;
    jira_epic_key?: string | null;
    hp_multiplier?: number;
    starts_at: string;
    ends_at: string;
    damage_event_types?: string[];
  }): Promise<{ boss: Boss }> {
    return api(`/api/admin/bosses`, { method: 'POST', body: input });
  },

  update(
    id: string,
    patch: {
      name?: string;
      jira_epic_key?: string | null;
      hp_multiplier?: number;
      starts_at?: string;
      ends_at?: string;
      damage_event_types?: string[];
      reward_badge_id?: string | null;
    },
  ): Promise<{ boss: Boss }> {
    return api(`/api/admin/bosses/${id}`, { method: 'PATCH', body: patch });
  },

  archive(id: string): Promise<{ archived: string }> {
    return api(`/api/admin/bosses/${id}`, { method: 'DELETE' });
  },

  // Phase 2: Jira epic import + latch + live progress.
  listJiraEpics(teamId: string): Promise<{ epics: JiraEpicSummary[] }> {
    return api(`/api/admin/teams/${teamId}/jira-epics`);
  },

  previewEpic(teamId: string, epicKey: string): Promise<{
    epic_key: string;
    hp_pool: BossHpPoolTicket[];
    total_points: number | null;
  }> {
    return api(`/api/admin/bosses/preview-epic?team_id=${encodeURIComponent(teamId)}&epicKey=${encodeURIComponent(epicKey)}`);
  },

  start(id: string): Promise<{ boss: Boss }> {
    return api(`/api/admin/bosses/${id}/start`, { method: 'POST' });
  },

  progress(id: string): Promise<{
    hp: number | null;
    damage_dealt: number;
    status: string;
    per_user_damage: { user_id: string; display_name: string; damage: number }[];
  }> {
    return api(`/api/admin/bosses/${id}/progress`);
  },

  // Phase 3: close (vanquished / survived) + badge reward.
  end(id: string): Promise<{
    boss: Boss;
    outcome: 'vanquished' | 'survived';
    damage_dealt: number;
    hp: number;
    badges_awarded: number;
  }> {
    return api(`/api/admin/bosses/${id}/end`, { method: 'POST' });
  },
};

export interface JiraEpicSummary {
  key: string;
  name: string;
  total_points: number | null;
  issue_count: number;
}
